/**
 * The gun's feedback DESIGN — every tone, colour and setting choice, in one
 * place, on the side that deploys.
 *
 * The tablet app (Tablet Browser; ID28 decision 0010) can make the DS3608 do exactly what
 * it is told and nothing more: Zebra's firmware offers the 27 beeper patterns
 * below, three LED colours, a volume and a decode-beep switch. That list is
 * the gun's vocabulary and lives in the bridge. WHAT TO SAY with it lives
 * here, because this is the half that ships with a git push; the bridge ships
 * by MDM. Retuning a dock is an edit to this file and a deploy, never an APK.
 *
 * Pure data and pure functions. Nothing here touches the device —
 * `gunCommands.ts` sends what these return — which is what makes the design
 * unit-testable on a desk with no gun.
 */

/** Every pattern the gun's beeper can make. The bridge refuses anything else. */
export const GUN_TONES = [
  'high-short-1', 'high-short-2', 'high-short-3', 'high-short-4', 'high-short-5',
  'low-short-1', 'low-short-2', 'low-short-3', 'low-short-4', 'low-short-5',
  'high-long-1', 'high-long-2', 'high-long-3', 'high-long-4', 'high-long-5',
  'low-long-1', 'low-long-2', 'low-long-3', 'low-long-4', 'low-long-5',
  'fast-warble', 'slow-warble',
  'high-low', 'low-high', 'high-low-high', 'low-high-low', 'high-high-low-low',
] as const

export type GunTone = typeof GUN_TONES[number]

/** `amber` is what the SDK calls the "other" LED; on a DS3608 it lights amber (verified 2026-09-01). */
export type GunColour = 'green' | 'red' | 'amber'

export type GunVolume = 'low' | 'medium' | 'high'

/**
 * What a signal MEANS. The screens speak in these; the table below says how
 * they sound. The first three are verdicts on a scan; `attention` is
 * unsolicited — the yard changed while nobody was scanning.
 */
export type ScannerSignal = 'ok' | 'warn' | 'error' | 'attention'

export interface GunCue {
  /** Absent means silent: the lamp alone carries the cue. */
  tone?: GunTone
  led?: { colour: GunColour, ms: number }
}

/**
 * The cues. Change these to change what the dock sounds like.
 *
 * A BEEP MEANS YES. `ok` is the one sound a scan can make; a refusal is the
 * red lamp and nothing else (user, 2026-09-02, replacing the five low beeps
 * of 2026-09-01). With the decode beep off as well, a driver who hears the
 * gun knows the pallet was accepted without looking, and a driver who hears
 * nothing looks — at a lamp that is red for longer than the green ever is,
 * so it tells the story from across a trailer.
 *
 * - `ok`: one high short beep — the same shape as a stock good-decode beep,
 *   so a driver hears "accepted" without learning anything new — and green.
 * - `error`: red, held; no sound.
 * - `warn`: red, shorter; no sound. Red, not amber: amber is the colour of
 *   `attention`, and a warning is still a verdict on a scan.
 * - `attention` is not a verdict, and is silent too — see below.
 */
export const GUN_CUES: Record<ScannerSignal, GunCue> = {
  ok: { tone: 'high-short-1', led: { colour: 'green', ms: 800 } },
  warn: { led: { colour: 'red', ms: 800 } },
  error: { led: { colour: 'red', ms: 1500 } },
  // Orders becoming loadable: amber, and NO sound (user, 2026-10-08,
  // replacing the three high beeps of 2026-09-01). Operators listen for a
  // sound and do not count it — three beeps and one are both "a beep", and a
  // beep means the pallet was accepted. A notice that beeped could land on a
  // refused scan and be heard as a yes. So the gun's ONLY sound is `ok`; the
  // tablet's bell and the screen carry this one. Amber is the SDK's "other"
  // LED and lights amber on the DS3608 (verified 2026-09-01).
  attention: { led: { colour: 'amber', ms: 1500 } },
}

/**
 * The same cues with every sound taken out: the lamps alone. What the tablet's Gun Sounds
 * setting turns off (Mark, 2026-10-16, Settings); a refusal is already silent, so off means
 * the accept is told by the green lamp only.
 */
export function withoutSound(cues: Record<ScannerSignal, GunCue>): Record<ScannerSignal, GunCue> {
  return Object.fromEntries(Object.entries(cues).map(([signal, cue]) =>
    [signal, cue.led ? { led: cue.led } : {}])) as Record<ScannerSignal, GunCue>
}

export interface GunSettings {
  volume: GunVolume
  /** Whether the gun beeps by itself on every decode, before the app has judged anything. */
  decodeBeep: boolean
}

/** The dock answer, and therefore the answer whenever nothing else is said. */
export const DEFAULT_GUN_VOLUME: GunVolume = 'high'

export function isGunVolume(value: unknown): value is GunVolume {
  return value === 'low' || value === 'medium' || value === 'high'
}

/**
 * The volume a build starts at, from `NUXT_PUBLIC_GUN_VOLUME`.
 *
 * Unset, misspelt, or anything unrecognised falls to HIGH. That is the whole
 * point of the default: the deploy workflows set no such variable, so a dock
 * is loud without anybody remembering to make it so, and a typo in a bench
 * `.env` is merely loud rather than silently quiet.
 */
export function resolveGunVolume(configured: string | null | undefined): GunVolume {
  const value = String(configured ?? '').trim().toLowerCase()
  return isGunVolume(value) ? value : DEFAULT_GUN_VOLUME
}

/**
 * Whether this build shows the screens that only exist for testing.
 *
 * Tied to the same variable deliberately (user, 2026-09-01): turning the gun
 * down is something only done at a bench, so it is as good a statement of
 * "this is not a dock" as a second flag would be, and one variable cannot
 * disagree with itself. Note that setting it explicitly to `high` means a
 * dock: loud, and no test screens.
 *
 * The Barcode gun screen is NOT gated by this. It carries the configuration
 * barcodes, which is exactly what somebody needs when a gun stops working on
 * a dock — hiding it would put the fix behind a rebuild.
 */
export function showsTestScreens(configured: string | null | undefined): boolean {
  return resolveGunVolume(configured) !== DEFAULT_GUN_VOLUME
}

/**
 * How the gun is set for this build.
 *
 * Decode beep is OFF everywhere (user, 2026-09-01): the only sounds are the
 * verdicts above, so a beep always means something.
 */
export function gunDefaults(configured: string | null | undefined): GunSettings {
  return {
    volume: resolveGunVolume(configured),
    decodeBeep: false,
  }
}

/**
 * The cue in words, for the strip's tone chip: what the gun just did, said
 * where the driver can read it — "1 beep · green lamp", "red lamp · no beep".
 * Read off the same table, so the words can never disagree with the sound.
 */
export function describeCue(signal: ScannerSignal, cues: Record<ScannerSignal, GunCue> = GUN_CUES): string {
  const cue = cues[signal]
  const parts: string[] = []
  if (cue.tone) {
    const count = /-(\d)$/.exec(cue.tone)
    const beeps = count ? Number(count[1]) : 1
    parts.push(`${beeps} beep${beeps === 1 ? '' : 's'}`)
  }
  if (cue.led) parts.push(`${cue.led.colour} lamp`)
  if (!cue.tone) parts.push('no beep')
  return parts.join(' · ')
}

/** One broadcast to the bridge: flat string extras, nothing nested. */
export type BridgeCommand = Record<string, string>

/** The broadcasts that make the gun perform one cue, in the order to send them: sound first, then lamp. */
export function cueCommands(signal: ScannerSignal, cues: Record<ScannerSignal, GunCue> = GUN_CUES): BridgeCommand[] {
  const cue = cues[signal]
  const commands: BridgeCommand[] = []
  if (cue.tone) commands.push({ command: 'beep', tone: cue.tone })
  if (cue.led) commands.push({ command: 'led', colour: cue.led.colour, ms: String(cue.led.ms) })
  return commands
}

/** The broadcasts that put the gun into a known state. Idempotent; safe to repeat. */
export function settingsCommands(settings: GunSettings): BridgeCommand[] {
  return [
    { command: 'volume', level: settings.volume },
    { command: 'decode-beep', value: settings.decodeBeep ? 'on' : 'off' },
  ]
}
